import React, { useState, useContext, useEffect } from "react";
import { Redirect } from "react-router";
import { Link } from "react-router-dom";

import { auth } from "_firebase/index";

import { AuthContext } from "features/Auth";
import { useQuery } from "utils/hooks";

import Button from "components/Button";
import Input from "components/Input";

import { checkValidity } from "utils/fields";
import { updateObject } from "utils";

import classes from "../style.module.scss";

import VoltaLogo from "assets/images/logo-color.svg";

export default ({ loginUser, error }) => {
    const searchParams = useQuery();

    const { user, setUser } = useContext(AuthContext);

    const [passwordResetSuccess, setPasswordResetSuccess] = useState(
        searchParams.get("passwordResetSuccess") === "true"
    );
    const [resetPasswordError, setResetPasswordError] = useState(searchParams.get("passwordResetError"));
    const [redirectToRegister, setRedirectToRegister] = useState(false);

    const [valid, setValid] = useState(false);
    const [processing, toggleProcessing] = useState(false);
    const [loginForm, setLoginForm] = useState({
        email: {
            name: "email",
            value: "",
            type: "email",
            label: "Email Address",
            placeholder: "Your Email",
            valid: false,
            validation: {
                required: true,
                isEmail: true,
            },
            validMessage: "",
            touched: false,
        },
        password: {
            name: "password",
            value: "",
            type: "password",
            label: "Password",
            placeholder: "Your Password",
            valid: false,
            validation: {
                required: true,
            },
            validMessage: "",
            touched: false,
        },
    });

    useEffect(() => {
        let timeout = null;

        if (resetPasswordError) {
            timeout = setTimeout(() => {
                timeout = setResetPasswordError(null);
            }, 5000);
        }

        return () => {
            if (timeout) clearTimeout(timeout);

            setResetPasswordError(null);
        };
    }, [resetPasswordError]);

    if (redirectToRegister) return <Redirect to="/register" />;
    if (!!user) return <Redirect to="/dashboard" />;

    const onChangeHandler = (value, fieldName) => {
        const { isValid, message } = checkValidity(loginForm[fieldName].label, value, loginForm[fieldName].validation);

        const updatedControls = updateObject(loginForm, {
            [fieldName]: updateObject(loginForm[fieldName], {
                value,
                valid: isValid,
                validMessage: message,
                touched: true,
            }),
        });

        setValid(Object.values(updatedControls).every(({ valid }) => valid));
        setLoginForm(updatedControls);
    };

    return (
        <div className={classes.authWrapper}>
            <div className={classes.formWrapper}>
                <div className={classes.top}>
                    <img src={VoltaLogo} alt="Volta Snap logo in colour" />
                    <h2>Login</h2>
                    {/* TODO: Prohibit login from google if no user exists in database, currently it creates a new auth user */}
                    {/* <Button type='hollow' onClick={() => signInWithGoogle()}>
                        Log in With Google
                    </Button> */}
                </div>
                {passwordResetSuccess && (
                    <h3 style={{ marginBottom: "1rem", color: "#0eb0a9", fontSize: "14px" }}>
                        Your password has been successfully reset.
                    </h3>
                )}
                <form className={classes.form}>
                    {Object.values(loginForm).map(
                        ({ name, type, label, valid, placeholder, validMessage, touched }) => (
                            <Input
                                key={name}
                                large
                                name={name}
                                type={type}
                                label={label}
                                placeholder={placeholder}
                                invalid={!valid}
                                validMessage={validMessage}
                                touched={touched}
                                className={classes.input}
                                input={{
                                    onChange: (value) => onChangeHandler(value, name),
                                }}
                            />
                        )
                    )}
                    <div>
                        <Button
                            disabled={!valid || processing}
                            type="submit"
                            onClick={async (event) => {
                                toggleProcessing(true);
                                const result = await loginUser(event, loginForm);

                                if (result?.registrationIncomplete) {
                                    setRedirectToRegister(true);
                                    toggleProcessing(false);
                                    return;
                                }

                                setUser(auth.currentUser);

                                toggleProcessing(false);
                            }}
                        >
                            {processing ? "Logging In..." : "Log In"}
                        </Button>
                    </div>
                    {(error !== null || resetPasswordError !== null) && (
                        <p className={classes.error}>{error || resetPasswordError}</p>
                    )}
                    <p>
                        Don't have an account? <Link to="/register">Sign Up.</Link>
                    </p>
                    <p>
                        <Link to="/password-reset">Forgot Password?</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};
